import { ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconExclamationCircle,
    IconPlayerPause,
    IconPlayerPlay,
    IconPlus,
    IconTrash,
    IconWebhook,
    IconX,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { WebhookResponse } from '@xver/apis/v1/sammeln/webhooks/model';
import Button from '@xver/components/Atoms/Button/Button';
import ConfirmActionModal from '@xver/components/Atoms/ConfirmActionModal/ConfirmActionModal';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WEBHOOKS_QUERY_KEY, useWebhooksQuery } from '../hooks/useWebhooksQuery';
import AddWebhookModal from './AddWebhookModal';

interface WebhooksTabPanelProps {
    projectIdOrName: string;
}

export default function WebhooksTabPanel({ projectIdOrName }: WebhooksTabPanelProps) {
    const { t } = useTranslation('artifactRegistry');
    const queryClient = useQueryClient();
    const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<WebhookResponse | null>(null);

    const { data, isPending } = useWebhooksQuery(projectIdOrName);
    const webhooks = data?.webhooks ?? [];

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [WEBHOOKS_QUERY_KEY, projectIdOrName] });

    const deleteMutation = useMutation({
        mutationFn: (webhook: WebhookResponse) =>
            Endpoints.v1.Sammeln.Webhooks.deleteWebhook(projectIdOrName, webhook.id),
        onSuccess: async () => {
            notifications.show({
                title: t('webhooks.toast.deleteTitle', 'Webhook deleted'),
                message: t(
                    'webhooks.toast.deleteMessage',
                    'The webhook will no longer receive events.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('webhooks.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('webhooks.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const toggleMutation = useMutation({
        mutationFn: (webhook: WebhookResponse) =>
            Endpoints.v1.Sammeln.Webhooks.updateWebhook(projectIdOrName, webhook.id, {
                name: webhook.name,
                description: webhook.description,
                targets: webhook.targets.map((tg) => ({
                    type: tg.type,
                    address: tg.address,
                    auth_header: tg.auth_header,
                    skip_cert_verify: tg.skip_cert_verify,
                    payload_format: tg.payload_format,
                })),
                event_types: webhook.event_types,
                enabled: !webhook.enabled,
            }),
        onSuccess: async (_, webhook) => {
            notifications.show({
                title: webhook.enabled
                    ? t('webhooks.toast.disabledTitle', 'Webhook disabled')
                    : t('webhooks.toast.enabledTitle', 'Webhook enabled'),
                message: t('webhooks.toast.updatedMessage', 'The webhook was updated.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await invalidate();
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('webhooks.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('webhooks.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleDelete = (webhook: WebhookResponse) => {
        setPendingDelete(webhook);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const columns: DataTableColumn<WebhookResponse>[] = [
        {
            key: 'name',
            label: t('webhooks.columns.name', 'Name'),
            render: (w) => <span className="font-medium text-fg">{w.name}</span>,
        },
        {
            key: 'description',
            label: t('webhooks.columns.description', 'Description'),
            render: (w) => (
                <span className="text-xs text-fg/68 truncate max-w-[260px] block">
                    {w.description || '-'}
                </span>
            ),
        },
        {
            key: 'enabled',
            label: t('webhooks.columns.enabled', 'Enabled'),
            render: (w) =>
                w.enabled ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400 light:text-green-800">
                        <IconCheck size={14} />
                        {t('webhooks.enabledTrue', 'Enabled')}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-fg/68">
                        <IconX size={14} />
                        {t('webhooks.enabledFalse', 'Disabled')}
                    </span>
                ),
        },
        {
            key: 'event_types',
            label: t('webhooks.columns.events', 'Events'),
            render: (w) => <span className="text-xs text-fg/78">{w.event_types?.length ?? 0}</span>,
        },
        {
            key: 'targets',
            label: t('webhooks.columns.targets', 'Targets'),
            render: (w) => <span className="text-xs text-fg/78">{w.targets?.length ?? 0}</span>,
        },
    ];

    return (
        <>
            <DialogBox
                label={t('webhooks.title', 'Webhooks')}
                description={t(
                    'webhooks.description',
                    'Receive notifications when registry events occur.'
                )}
                rightDescription={
                    <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<IconPlus size={16} />}
                        onClick={openAdd}
                    >
                        {t('webhooks.add', 'Add webhook')}
                    </Button>
                }
            >
                <DataTable
                    caption={t('webhooks.title', 'Webhooks')}
                    columns={columns}
                    rows={webhooks}
                    isLoading={isPending}
                    rowKey={(w) => w.id}
                    emptyMessage={
                        <div className="w-full flex flex-col items-center justify-center gap-2 py-6">
                            <IconWebhook size={28} stroke={1.25} className="text-fg/40" />
                            <span className="text-sm text-fg/68">
                                {t(
                                    'webhooks.empty',
                                    'No webhooks configured. Click Add webhook to create one.'
                                )}
                            </span>
                        </div>
                    }
                    actions={(w) => (
                        <div className="flex flex-row justify-end items-center gap-1">
                            <Tooltip
                                label={
                                    w.enabled
                                        ? t('webhooks.actions.disable', 'Disable')
                                        : t('webhooks.actions.enable', 'Enable')
                                }
                                withArrow
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleMutation.mutate(w);
                                    }}
                                    disabled={toggleMutation.isPending}
                                    aria-label={
                                        w.enabled
                                            ? t('webhooks.actions.disable', 'Disable')
                                            : t('webhooks.actions.enable', 'Enable')
                                    }
                                >
                                    {w.enabled ? (
                                        <IconPlayerPause size={16} stroke={1.5} />
                                    ) : (
                                        <IconPlayerPlay size={16} stroke={1.5} />
                                    )}
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip
                                label={t('webhooks.actions.delete', 'Delete webhook')}
                                withArrow
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(w);
                                    }}
                                    disabled={deleteMutation.isPending}
                                    aria-label={t('webhooks.actions.deleteAria', 'Delete webhook')}
                                >
                                    <IconTrash size={16} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}
                />
            </DialogBox>

            <AddWebhookModal
                opened={addOpened}
                onClose={closeAdd}
                projectIdOrName={projectIdOrName}
            />

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('webhooks.confirm.deleteTitle', 'Delete webhook?')}
                message={t('webhooks.confirm.deleteMessage', {
                    name: pendingDelete?.name ?? '',
                    defaultValue: '"{{name}}" will stop receiving events.',
                })}
                confirmLabel={t('webhooks.confirm.deleteLabel', 'Delete webhook')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </>
    );
}
